/**
 * PlayerProfile — front-end "shell" view that consolidates stats from
 * one or more roster entries (player_profile records) into a single
 * profile identity.
 *
 * Routed at /stats/profile/:profileId. Mirrors the visual language of
 * PlayerStats.js but the data source is a `linked_profiles` shell + the
 * aggregated games returned by GET /api/profiles/{id}/games.
 *
 * The header has a 🔗 icon that opens ProfileLinkManagementModal so the
 * user can add/remove member rosters without leaving the page.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/lib/api';
import { getPctColorHex } from '@/lib/statColors';
import { ArrowLeft, Link2, Trash2, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import GameAnalysisModal from '@/components/GameAnalysisModal';
import ProfileLinkManagementModal from '@/components/ProfileLinkManagementModal';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const NAVY = '#0B1824';
const ORANGE = '#e8963a';
const MUTED = '#8FA3B1';
const BORDER = '#DDE5EC';

/* Shooting-split colours live in /app/frontend/src/lib/statColors.js
   (build 210). Local `splitColor` is a thin adapter mapping the UI's
   tile label to the shared tier-key. 2PT% has no dedicated tier so we
   score it on the FG% scale (closest semantic match). */
function splitColor(stat, value) {
  const type = stat === '2pt' ? 'fg' : stat;
  return getPctColorHex(value, undefined, type);
}

const fmtAvg = (n, d) => (d > 0 ? (n / d).toFixed(1) : null);
const fmtPct = (n, d) => (d > 0 ? Math.round((n / d) * 100) : null);

export default function PlayerProfile() {
  const { profileId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('last5');
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [analysisGame, setAnalysisGame] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  /* ── Fetch shell + aggregated games ── */
  const refresh = async () => {
    if (!profileId) return;
    setLoading(true);
    try {
      const [pRes, gRes] = await Promise.all([
        api.get(`/profiles/${profileId}`),
        api.get(`/profiles/${profileId}/games`).catch(() => ({ data: { games: [] } })),
      ]);
      setProfile(pRes.data);
      setGames(gRes.data?.games || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load profile');
      navigate('/stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [profileId]);

  /* ── Filter window ── */
  const sortedGames = useMemo(
    () => [...games].sort((a, b) => new Date(b.date_time) - new Date(a.date_time)),
    [games]
  );
  const filteredGames = useMemo(() => {
    if (filter === 'last5')  return sortedGames.slice(0, 5);
    if (filter === 'last10') return sortedGames.slice(0, 10);
    return sortedGames;
  }, [sortedGames, filter]);

  /* ── Aggregate ── */
  const buildAgg = (gameSet) => {
    const z = { gp: 0, pts:0, reb:0, oreb:0, dreb:0, ast:0, stl:0, blk:0, defl:0, to:0, pf:0,
                fgm:0, fga:0, three_pm:0, three_pa:0, ftm:0, fta:0, two_pm:0, two_pa:0 };
    for (const g of gameSet) {
      const r = g.box_row; if (!r) continue;
      z.gp += 1;
      z.pts += r.pts || 0;
      z.reb += r.reb || ((r.oreb || 0) + (r.dreb || 0));
      z.oreb += r.oreb || 0;
      z.dreb += r.dreb || 0;
      z.ast += r.ast || 0;
      z.stl += r.stl || 0;
      z.blk += r.blk || 0;
      z.defl += r.defl || 0;
      z.to += r.to || 0;
      z.pf += r.pf || 0;
      z.fgm += r.fgm || 0;
      z.fga += r.fga || 0;
      z.three_pm += r.three_pm || 0;
      z.three_pa += r.three_pa || 0;
      z.ftm += r.ftm || 0;
      z.fta += r.fta || 0;
      z.two_pm += (r.two_pm != null ? r.two_pm : (r.fgm || 0) - (r.three_pm || 0));
      z.two_pa += (r.two_pa != null ? r.two_pa : (r.fga || 0) - (r.three_pa || 0));
    }
    return z;
  };
  const agg = useMemo(() => buildAgg(filteredGames), [filteredGames]);
  const allTimeAgg = useMemo(() => buildAgg(sortedGames), [sortedGames]);

  const perGame = useMemo(() => ({
    pts:  fmtAvg(agg.pts, agg.gp),
    reb:  fmtAvg(agg.reb, agg.gp),
    ast:  fmtAvg(agg.ast, agg.gp),
    stl:  fmtAvg(agg.stl, agg.gp),
    blk:  fmtAvg(agg.blk, agg.gp),
    defl: fmtAvg(agg.defl, agg.gp),
    oreb: fmtAvg(agg.oreb, agg.gp),
    dreb: fmtAvg(agg.dreb, agg.gp),
    to:   fmtAvg(agg.to, agg.gp),
    pf:   fmtAvg(agg.pf, agg.gp),
  }), [agg]);

  const splits = useMemo(() => {
    // True Shooting % = pts / (2 * (FGA + 0.44 * FTA)).
    // Returns null when the denominator is zero so the tile renders NA.
    const tsDenom = 2 * (agg.fga + 0.44 * agg.fta);
    const tsPct = tsDenom > 0 ? Math.round((agg.pts / tsDenom) * 100) : null;
    return {
      twoPct:   fmtPct(agg.two_pm,   agg.two_pa),
      threePct: fmtPct(agg.three_pm, agg.three_pa),
      ftPct:    fmtPct(agg.ftm,      agg.fta),
      fgPct:    fmtPct(agg.fgm,      agg.fga),
      eFG:      agg.fga > 0 ? Math.round(((agg.fgm + 0.5 * agg.three_pm) / agg.fga) * 100) : null,
      tsPct,
    };
  }, [agg]);

  const ats = useMemo(() => {
    const a = allTimeAgg;
    const num = (v, d) => (d > 0 ? Math.round((v / d) * 10) / 10 : null);
    const pct = (v, d) => (d > 0 ? Math.round((v / d) * 100 * 10) / 10 : null);
    return {
      averages: {
        ppg: num(a.pts, a.gp), rpg: num(a.reb, a.gp), apg: num(a.ast, a.gp),
        spg: num(a.stl, a.gp), bpg: num(a.blk, a.gp), topg: num(a.to, a.gp),
        two_pct: pct(a.two_pm, a.two_pa), three_p_pct: pct(a.three_pm, a.three_pa),
        ft_pct: pct(a.ftm, a.fta), fg_pct: pct(a.fgm, a.fga),
      },
      totals: {
        fgm: a.fgm, fga: a.fga, three_pm: a.three_pm, three_pa: a.three_pa,
        ftm: a.ftm, fta: a.fta,
      },
    };
  }, [allTimeAgg]);

  /* ── Per-member all-time averages ──
     The Game Breakdown modal compares a single game vs the player's
     all-time numbers. In the consolidated shell view "the player" for a
     given game is the specific member roster entry that played it (e.g.
     same kid, club team vs school team). We split the ats by member so
     the comparison is meaningful — comparing an AAU game vs the kid's
     AAU averages, not a blended cross-team baseline. */
  const atsByMember = useMemo(() => {
    const num = (v, d) => (d > 0 ? Math.round((v / d) * 10) / 10 : null);
    const pct = (v, d) => (d > 0 ? Math.round((v / d) * 100 * 10) / 10 : null);
    const blank = () => ({
      gp:0, pts:0, reb:0, oreb:0, dreb:0, ast:0, stl:0, blk:0, defl:0, to:0, pf:0,
      fgm:0, fga:0, three_pm:0, three_pa:0, ftm:0, fta:0, two_pm:0, two_pa:0,
    });
    const buckets = {};
    for (const g of sortedGames) {
      const mid = g.member_player_id;
      if (!mid) continue;
      const r = g.box_row; if (!r) continue;
      if (!buckets[mid]) buckets[mid] = blank();
      const z = buckets[mid];
      z.gp += 1;
      z.pts += r.pts || 0;
      z.reb += r.reb != null ? r.reb : ((r.oreb || 0) + (r.dreb || 0));
      z.oreb += r.oreb || 0;
      z.dreb += r.dreb || 0;
      z.ast += r.ast || 0;
      z.stl += r.stl || 0;
      z.blk += r.blk || 0;
      z.defl += r.defl || 0;
      z.to += r.to || 0;
      z.pf += r.pf || 0;
      z.fgm += r.fgm || 0;
      z.fga += r.fga || 0;
      z.three_pm += r.three_pm || 0;
      z.three_pa += r.three_pa || 0;
      z.ftm += r.ftm || 0;
      z.fta += r.fta || 0;
      z.two_pm += (r.two_pm != null ? r.two_pm : (r.fgm || 0) - (r.three_pm || 0));
      z.two_pa += (r.two_pa != null ? r.two_pa : (r.fga || 0) - (r.three_pa || 0));
    }
    const out = {};
    for (const [mid, a] of Object.entries(buckets)) {
      out[mid] = {
        averages: {
          ppg: num(a.pts, a.gp), rpg: num(a.reb, a.gp), apg: num(a.ast, a.gp),
          spg: num(a.stl, a.gp), bpg: num(a.blk, a.gp), topg: num(a.to, a.gp),
          two_pct: pct(a.two_pm, a.two_pa), three_p_pct: pct(a.three_pm, a.three_pa),
          ft_pct: pct(a.ftm, a.fta), fg_pct: pct(a.fgm, a.fga),
        },
        totals: {
          fgm: a.fgm, fga: a.fga, three_pm: a.three_pm, three_pa: a.three_pa,
          ftm: a.ftm, fta: a.fta,
        },
      };
    }
    return out;
  }, [sortedGames]);

  const totalTracked = sortedGames.length;
  const teamsLine = (profile?.members || [])
    .map(m => m.team_name).filter(Boolean).join(' · ');

  const deleteShell = async () => {
    try {
      await api.delete(`/profiles/${profileId}`);
      toast.success('Profile deleted');
      navigate('/stats');
    } catch {
      toast.error('Could not delete');
    }
  };

  if (loading || !profile) {
    return (
      <div className="px-4 py-12 text-center" data-testid="player-profile-loading" style={{ color: MUTED }}>
        Loading…
      </div>
    );
  }

  /* ── Render ── */
  return (
    <div className="-mx-4 sm:-mx-6 lg:mx-0 lg:max-w-2xl" data-testid="player-profile-page">
      {/* Back nav lives above the navy block on a neutral strip — keeps
          the player block's visual weight focused entirely on identity. */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,.06)' }}>
        <button
          onClick={() => navigate('/stats')}
          data-testid="player-profile-back"
          className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[.06em]"
          style={{ color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", background: 'none', border: 'none' }}
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLinkModalOpen(true)}
            data-testid="player-profile-link-btn"
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-full transition-all active:scale-[.94]"
            style={{ background: '#F1F5F9', border: `1px solid ${BORDER}` }}
            aria-label="Manage linked teams"
          >
            <Link2 className="w-4 h-4" style={{ color: NAVY }} />
            {(profile.members?.length || 0) > 0 && (
              <span
                data-testid="player-profile-link-count"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {profile.members.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setConfirmDelete(true)}
            data-testid="player-profile-delete-btn"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full transition-all active:scale-[.94]"
            style={{ background: '#F1F5F9', border: `1px solid ${BORDER}` }}
            aria-label="Delete profile"
          >
            <Trash2 className="w-4 h-4" style={{ color: NAVY }} />
          </button>
        </div>
      </div>

      {/* HEADER — pure player identity now that the back nav is hoisted
          out. Avatar / name / stats all scale up to fill the breathing
          room and balance the wider stat block on the right. */}
      <div className="px-4 pt-5 pb-5"
        style={{ background: NAVY, borderBottom: `2px solid ${ORANGE}` }}>
        <div className="flex items-center gap-3.5">
          <div className="w-[68px] h-[68px] rounded-full flex items-center justify-center shrink-0"
            style={{ background: ORANGE, color: NAVY, fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 33 }}
            data-testid="player-profile-avatar">
            {profile.jersey_number || '–'}
          </div>

          <div className="flex-1">
            <p
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: '#fff', lineHeight: 1, letterSpacing: '.01em', fontSize: 27 }}
              data-testid="player-profile-name">
              {(profile.name || 'PROFILE').toUpperCase()}
            </p>
            <p className="mt-1" style={{ color: 'rgba(255,255,255,.6)', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '.06em', fontWeight: 700, fontSize: 15, whiteSpace: 'normal' }}>
              {teamsLine ? teamsLine.toUpperCase() : 'NO TEAMS LINKED'}
            </p>
            <span className="inline-block mt-1.5 px-2.5 py-0.5 rounded-full whitespace-nowrap"
              style={{ background: 'rgba(255,255,255,.1)', color: 'rgba(255,255,255,.65)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.08em', fontSize: 13 }}
              data-testid="player-profile-games-tracked-badge">
              {totalTracked} GAME{totalTracked === 1 ? '' : 'S'} TRACKED
            </span>
          </div>
        </div>

        {profile.description && (
          <p className="mt-3 truncate" style={{ color: 'rgba(255,255,255,.5)', fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15 }}>
            {profile.description}
          </p>
        )}
      </div>

      {/* ── FILTER PILLS ── */}
      <div className="px-3 py-3 flex gap-2"
        style={{ background: '#fff', borderBottom: '1px solid rgba(0,0,0,.06)' }}
        data-testid="player-profile-filters">
        {[
          { k: 'last5',  label: 'Last 5' },
          { k: 'last10', label: 'Last 10' },
          { k: 'all',    label: 'All Time' },
        ].map(f => (
          <button key={f.k}
            onClick={() => setFilter(f.k)}
            data-testid={`pp-filter-${f.k}`}
            className="h-10 px-4 rounded-full text-[13px] font-bold uppercase tracking-[.06em] transition-all"
            style={{
              background: filter === f.k ? NAVY : '#F1F5F9',
              color: filter === f.k ? '#fff' : 'rgba(0,0,0,.45)',
              border: `1px solid ${filter === f.k ? NAVY : 'rgba(0,0,0,.1)'}`,
              fontFamily: "'Barlow Condensed', sans-serif",
            }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* ── PER GAME AVERAGES ── */}
      <Section title="Per Game Averages" testid="pp-section-per-game">
        <Row5
          cells={[
            { label: 'PTS',  val: perGame.pts,  highlight: true },
            { label: 'REB',  val: perGame.reb },
            { label: 'AST',  val: perGame.ast },
            { label: 'STL',  val: perGame.stl },
            { label: 'BLK',  val: perGame.blk },
          ]}
        />
        <div className="h-2.5" />
        <Row5
          cells={[
            { label: 'DEFL', val: perGame.defl },
            { label: 'OREB', val: perGame.oreb },
            { label: 'DREB', val: perGame.dreb },
            { label: 'TO',   val: perGame.to },
            { label: 'FOUL', val: perGame.pf },
          ]}
        />
      </Section>

      {/* ── SHOOTING SPLITS ── */}
      <Section title="Shooting Splits" testid="pp-section-splits">
        <div className="grid grid-cols-3 gap-2">
          <SplitTile label="2PT%" pct={splits.twoPct}   color={splitColor('2pt', splits.twoPct)}   />
          <SplitTile label="3PT%" pct={splits.threePct} color={splitColor('3pt', splits.threePct)} />
          <SplitTile label="FT%"  pct={splits.ftPct}    color={splitColor('ft',  splits.ftPct)}    />
          <SplitTile label="FG%"  pct={splits.fgPct}    color={splitColor('fg',  splits.fgPct)}    />
          <SplitTile label="eFG%" pct={splits.eFG}      color={splitColor('efg', splits.eFG)}      />
          <SplitTile label="TS%"  pct={splits.tsPct}    color={splitColor('ts',  splits.tsPct)}    />
        </div>
      </Section>

      {/* ── GAME LOG ── */}
      <Section title="Game Log" testid="pp-section-gamelog">
        {filteredGames.length === 0 ? (
          <p className="text-[13px]" style={{ color: MUTED }}>No games tracked yet for this profile.</p>
        ) : (
          filteredGames.map(g => {
            const r = g.box_row; if (!r) return null;
            const won = g.is_home ? (g.home_score >= g.away_score) : (g.away_score >= g.home_score);
            const us = g.is_home ? g.home_score : g.away_score;
            const them = g.is_home ? g.away_score : g.home_score;
            const dateStr = g.date_time ? new Date(g.date_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
            const oppName = (g.opponent_name || 'Opponent').toUpperCase();
            const rowKey = `${g.member_player_id}::${g.game_id}`;
            return (
              <div
                key={rowKey}
                data-testid={`pp-game-row-${rowKey}`}
                className="py-3"
                style={{ borderBottom: '1px solid rgba(0,0,0,.06)' }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] truncate"
                      style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
                      vs {oppName}
                    </p>
                    <p className="text-[14px] mt-0.5" style={{ color: 'rgba(0,0,0,.45)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>
                      {dateStr} · {won ? 'W' : 'L'} {us}-{them}{g.team_name ? ` · ${g.team_name}` : ''}
                    </p>
                  </div>
                  <div className="flex items-end gap-4 shrink-0">
                    {[
                      { label: 'PTS', val: r.pts || 0 },
                      { label: 'REB', val: r.reb != null ? r.reb : ((r.oreb || 0) + (r.dreb || 0)) },
                      { label: 'AST', val: r.ast || 0 },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <div className="text-[18px] leading-none"
                          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: NAVY }}>
                          {s.val}
                        </div>
                        <div className="text-[11px] mt-1"
                          style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Game Breakdown CTA — opens the per-player breakdown
                    modal for this single game. Scoped explicitly to the
                    member roster entry that played this game so the
                    modal aggregates against THAT player's all-time
                    averages, not the consolidated shell totals. */}
                <button
                  type="button"
                  onClick={() => setAnalysisGame({ game: g, row: r })}
                  data-testid={`pp-game-breakdown-${rowKey}`}
                  className="mt-2 ml-auto flex items-center gap-1 h-8 px-3 rounded-full transition-all active:scale-[.97]"
                  style={{
                    background: '#F1F5F9',
                    color: NAVY,
                    border: `1px solid ${BORDER}`,
                    fontFamily: "'Barlow Condensed', sans-serif",
                    fontWeight: 800,
                    fontSize: 11,
                    letterSpacing: '.10em',
                  }}
                >
                  GAME BREAKDOWN <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })
        )}
      </Section>

      <GameAnalysisModal
        open={!!analysisGame}
        onOpenChange={(o) => { if (!o) setAnalysisGame(null); }}
        game={analysisGame ? { ...analysisGame.row } : {}}
        ats={
          analysisGame
            ? (atsByMember[analysisGame.game?.member_player_id] || ats)
            : ats
        }
        opponentName={analysisGame?.game?.opponent_name || 'Opponent'}
        gameDate={analysisGame?.game?.date_time
          ? new Date(analysisGame.game.date_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
          : ''}
      />

      <ProfileLinkManagementModal
        open={linkModalOpen}
        onOpenChange={setLinkModalOpen}
        profile={profile}
        onChange={() => refresh()}
      />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md" data-testid="player-profile-delete-modal">
          <DialogHeader>
            <DialogTitle
              className="text-lg font-bold uppercase tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Delete Profile?
            </DialogTitle>
            <DialogDescription className="text-[13px]">
              This removes the <strong>{profile.name}</strong> profile shell.
              The underlying team rosters and game stats are NOT affected —
              you can re-create the profile any time.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(false)}
              className="flex-1 h-11 rounded-full"
              data-testid="player-profile-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={deleteShell}
              data-testid="player-profile-delete-confirm"
              className="flex-1 h-11 rounded-full font-bold uppercase"
              style={{ background: '#dc2626', color: '#fff' }}
            >
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SplitTile({ label, pct, color }) {
  const isNA = pct == null;
  return (
    <div className="rounded-[12px] py-4 px-2 text-center"
      style={{ background: '#F1F5F9' }}
      data-testid={`pp-split-${label.toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
      {isNA ? (
        <div className="text-[24px] leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: 'rgba(0,0,0,.2)' }}>
          NA
        </div>
      ) : (
        <div className="text-[34px] leading-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: color || NAVY }}>
          {pct}%
        </div>
      )}
      <div className="text-[12px] mt-2" style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.08em' }}>
        {label}
      </div>
    </div>
  );
}

/* ── Layout helpers — kept identical to PlayerStats so both views render
   pixel-perfect with the same rhythm and section chrome. ── */
function Section({ title, children, testid }) {
  return (
    <div className="px-4 py-4 mb-2" style={{ background: '#fff' }} data-testid={testid}>
      <div className="text-[13px] mb-3"
        style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.10em' }}>
        {title.toUpperCase()}
      </div>
      {children}
    </div>
  );
}

function Row5({ cells }) {
  return (
    <div className="grid grid-cols-5 gap-1">
      {cells.map(c => (
        <div key={c.label} className="text-center">
          {c.val == null ? (
            <div className="text-[20px] leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: 'rgba(0,0,0,.25)' }}>
              NA
            </div>
          ) : (
            <div className="text-[28px] leading-none"
              style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, color: c.highlight ? ORANGE : NAVY }}>
              {c.val}
            </div>
          )}
          <div className="text-[12px] mt-1.5" style={{ color: 'rgba(0,0,0,.4)', fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, letterSpacing: '.06em' }}>
            {c.label}
          </div>
        </div>
      ))}
    </div>
  );
}
